import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AiReply, AiService, AiSource, AiStreamEvent } from 'src/app/services/ai.service';
import { AiOnboardingService } from 'src/app/services/ai-onboarding.service';
import { Domain } from 'src/app/services/domain.service';
import { selectDomain } from 'src/app/store/selectors/auth.selectors';
import { canUseMessaging } from 'src/app/utils/messaging-access';

interface AiMessage {
  role: 'user' | 'assistant';
  content?: string;
  reply?: AiReply;
  sources?: AiSource[];
  sourceGroups?: AiSourceGroup[];
  error?: string;
  /** Clarification option or follow-up the user clicked on this message. */
  selectedOption?: string;
}

interface AiSourceItem extends AiSource {
  key: string;
  passage: string;
}

interface AiSourceGroup {
  key: string;
  title: string;
  source_url?: string;
  items: AiSourceItem[];
}

const CONTACT_CSE_LINK_PATTERN = /\[\[CONTACT_CSE_LINK\]\]([^\r\n]*?)\[\[\/CONTACT_CSE_LINK\]\]/gi;
const CONTACT_CSE_MARKER_PATTERN = /\[\[\/?CONTACT_CSE_LINK\]\]/gi;

@Component({
  selector: 'mvp-ai-assistant-overlay',
  templateUrl: './ai-assistant-overlay.component.html',
  styleUrls: ['./ai-assistant-overlay.component.scss'],
})
export class AiAssistantOverlayComponent implements OnInit, OnChanges, OnDestroy {
  @Input() firstMessage?: string;
  /** The first message is the guided tour's sample question. */
  @Input() onboarding = false;
  @Output() close = new EventEmitter<void>();
  @ViewChild('thread') thread?: ElementRef<HTMLDivElement>;

  domain?: Domain;
  question = '';
  messages: AiMessage[] = [];
  conversationId?: number;
  loading = false;
  receivingText = false;
  openSources: Record<string, boolean> = {};
  /** Streamed text not yet on screen; revealed a few characters per frame. */
  private pendingStreamText = '';
  private revealFrame?: { frame: number } | { timer: ReturnType<typeof setTimeout> };
  private revealTarget?: AiMessage;
  private consumedFirstMessage?: string;
  private streamSubscription?: Subscription;

  constructor(
    private aiService: AiService,
    private aiOnboardingService: AiOnboardingService,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private router: Router,
  ) {}

  /** Step 2 of the guided tour, shown as soon as the assistant opens on the sample question. */
  get showOnboardingBanner(): boolean {
    return this.aiOnboardingService.step === 'answer';
  }

  get onboardingTotalSteps(): number {
    return this.aiOnboardingService.totalSteps;
  }

  get onboardingDots(): number[] {
    return Array.from({ length: this.onboardingTotalSteps }, (_, index) => index + 1);
  }

  dismissOnboardingBanner() {
    this.aiOnboardingService.dismissAnswerBubble();
  }

  ngOnInit() {
    if (typeof document !== 'undefined') {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }

    this.store.select(selectDomain).pipe(take(1)).subscribe((domain: Domain) => {
      this.domain = domain;
      this.sendFirstMessage();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('firstMessage' in changes) {
      this.sendFirstMessage();
    }
  }

  ngOnDestroy() {
    this.streamSubscription?.unsubscribe();
    this.cancelReveal();
  }

  resetConversation() {
    this.streamSubscription?.unsubscribe();
    this.cancelReveal();
    this.loading = false;
    this.receivingText = false;
    this.messages = [];
    this.conversationId = undefined;
    this.openSources = {};
  }

  /**
   * True while the assistant waits for one of its options to be clicked
   * (multi-CCN demo domains): the composer is disabled, only option buttons
   * can send.
   */
  get composerLocked(): boolean {
    if (this.loading) {
      return false;
    }

    const last = this.messages[this.messages.length - 1];
    return last?.role === 'assistant' && !!last.reply?.input_locked;
  }

  /**
   * True once the conversation moved past this assistant message: its
   * options were either clicked or bypassed by a typed question.
   */
  isAnswered(messageIndex: number): boolean {
    return messageIndex < this.messages.length - 1;
  }

  /** An option that was offered but not picked: shown greyed out. */
  isOptionMuted(message: AiMessage, messageIndex: number, option: string): boolean {
    return this.isAnswered(messageIndex) && message.selectedOption !== option;
  }

  send(explicitQuestion?: string, source?: AiMessage, onboarding = false) {
    if (!explicitQuestion && this.composerLocked) {
      return;
    }

    const question = (explicitQuestion || this.question || '').trim();
    if (!question || this.loading) {
      return;
    }

    if (source && explicitQuestion) {
      source.selectedOption = explicitQuestion;
    }

    this.messages.push({ role: 'user', content: question });
    this.question = '';
    this.loading = true;
    this.receivingText = false;
    this.cdr.detectChanges();
    this.scrollToLatestQuestion();

    let streamedMessage: AiMessage | undefined;
    let structuredData: AiStreamEvent['data'] | undefined;
    let streamFinished = false;
    let streamFailed = false;

    const ensureStreamedMessage = (): AiMessage => {
      if (!streamedMessage) {
        streamedMessage = { role: 'assistant', reply: { main: '' }, sources: [] };
        this.messages.push(streamedMessage);
      }

      return streamedMessage;
    };

    const applyStructuredData = () => {
      if (!structuredData?.reply) {
        return;
      }

      const message = ensureStreamedMessage();
      const sources = this.mergeCitedAndRetrievedSources(
        structuredData.reply.sources,
        structuredData.rag_sources,
      );
      message.reply = structuredData.reply;
      message.sources = sources;
      message.sourceGroups = this.buildSourceGroups(sources, this.messages.indexOf(message));
      structuredData = undefined;
    };

    const finishIfReady = () => {
      if (!streamFinished) {
        return;
      }

      // Whatever is still queued goes on screen before the final block.
      this.flushReveal();
      if (!streamFailed) {
        applyStructuredData();
      }
      this.loading = false;
      this.receivingText = false;
      // No forced jump here: the sources and follow-ups settle under the
      // text the reader is on, as chat clients do.
      this.cdr.detectChanges();
    };

    this.streamSubscription = this.aiService.stream(question, this.domain?.uuid, this.conversationId, { onboarding }).subscribe({
      next: (event: AiStreamEvent) => {
        if (event.event === 'meta') {
          this.conversationId = event.data.conversation_id || this.conversationId;
          return;
        }

        if (event.event === 'text' && event.data.text) {
          // The API streams the answer as the model writes it. Network bursts
          // are evened out: the text is revealed a few characters per frame,
          // catching up quickly so it never lags behind the stream.
          this.receivingText = true;
          this.queueReveal(ensureStreamedMessage(), event.data.text);
          return;
        }

        if (event.event === 'structured' && event.data.reply) {
          structuredData = event.data;
          // Applied once the stream completes, after the queued text.
          return;
        }

        if (event.event === 'error') {
          structuredData = undefined;
          streamFailed = true;
          this.cancelReveal();
          const error = event.data.message || 'Une erreur est survenue, veuillez réessayer.';
          if (streamedMessage) {
            streamedMessage.error = error;
            streamedMessage.reply = undefined;
          } else {
            this.messages.push({ role: 'assistant', error });
          }
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        structuredData = undefined;
        streamFailed = true;
        streamFinished = true;
        this.cancelReveal();
        const error = this.errorMessage(err);
        if (streamedMessage) {
          streamedMessage.error = error;
          streamedMessage.reply = undefined;
        } else {
          this.messages.push({ role: 'assistant', error });
        }
        if (err?.status === 429) {
          // The search bar switches to the keyword search as soon as the
          // overlay closes; no need to wait for a fresh status.
          this.aiService.markQuotaExceeded(err?.error);
        } else {
          this.aiService.loadQuota(this.domain?.uuid);
        }
        finishIfReady();
      },
      complete: () => {
        streamFinished = true;
        // Every turn moves the counter (or refunds it): keep the shared
        // quota in step so the search bar shows the right remaining count.
        this.aiService.loadQuota(this.domain?.uuid);
        finishIfReady();
      },
    });
  }

  onComposerKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.send();
  }

  renderMarkdown(value?: string, linkContactCse = false): string {
    if (!value) {
      return '';
    }

    const escaped = this.escapeHtml(value);
    const content = this.renderContactCseLinkMarkers(escaped, linkContactCse);
    const lines = content.split(/\r?\n/);
    const blocks: string[] = [];
    let listItems: string[] = [];

    const flushList = () => {
      if (!listItems.length) {
        return;
      }
      blocks.push(`<ul>${listItems.map(item => `<li>${this.inlineMarkdown(item)}</li>`).join('')}</ul>`);
      listItems = [];
    };

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) {
        flushList();
        continue;
      }

      const bullet = line.match(/^[-*]\s+(.+)$/);
      if (bullet) {
        listItems.push(bullet[1]);
        continue;
      }

      flushList();
      blocks.push(`<p>${this.inlineMarkdown(line)}</p>`);
    }

    flushList();

    return blocks.join('');
  }

  groupedSources(message: AiMessage): AiSourceGroup[] {
    if (Array.isArray(message.sourceGroups)) {
      return message.sourceGroups;
    }

    return this.buildSourceGroups(message.sources, this.messages.indexOf(message));
  }

  followUps(reply?: AiReply): string[] {
    const list = reply?.follow_up_questions;
    return Array.isArray(list)
      ? list.filter(item => typeof item === 'string' && item.trim()).slice(0, 3)
      : [];
  }

  /** The « contacter votre CSE » link opens the recipient list, so it follows the messaging rule. */
  canContactCse(): boolean {
    return canUseMessaging(this.domain);
  }

  /**
   * "Contacter votre CSE": the list of interlocutors, where the reader picks
   * who to write to, rather than a conversation opened straight on the
   * generic CSE address.
   */
  contactCse() {
    this.close.emit();
    this.router.navigate(['/new-chat']);
  }

  /**
   * Error bubbles are plain text, except that the quota message from the API
   * carries the same `[[CONTACT_CSE_LINK]]` markers as the assistant's replies
   * so that "votre CSE via la messagerie de Maviepro" opens the messaging.
   */
  renderErrorMessage(value?: string): string {
    if (!value) {
      return '';
    }

    return this.renderContactCseLinkMarkers(this.escapeHtml(value), this.canContactCse());
  }

  onAnswerClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-contact-cse-link]')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.contactCse();
  }

  openSourceUrl(event: Event, url?: string) {
    event.preventDefault();
    event.stopPropagation();

    if (!url) {
      return;
    }

    this.openInNewTab(url);
  }

  toggleSource(event: Event, key: string) {
    event.preventDefault();
    event.stopPropagation();

    this.openSources = {
      ...this.openSources,
      [key]: !this.openSources[key],
    };
    this.cdr.detectChanges();
  }

  openCitation(event: Event, item: AiSourceItem) {
    event.preventDefault();
    event.stopPropagation();

    if (item.viewer_url) {
      this.openSourceUrl(event, item.viewer_url);
      return;
    }

    this.toggleSource(event, item.key);
  }

  private sendFirstMessage() {
    const firstMessage = (this.firstMessage || '').trim();
    if (!firstMessage || this.consumedFirstMessage === firstMessage || !this.domain) {
      return;
    }

    this.consumedFirstMessage = firstMessage;
    this.send(firstMessage, undefined, this.onboarding);
  }

  private errorMessage(err: any): string {
    const response = err?.error || {};
    if (err?.status === 429) {
      if (response.message) {
        return response.message;
      }
      const period = response.period_label ? ` pour le mois de ${response.period_label}` : '';
      return `Vous avez atteint votre quota de questions${period}.`;
    }
    if (err?.status === 403) {
      return response.message || 'Accès refusé : rôle insuffisant pour interroger l’assistant.';
    }
    return 'Une erreur est survenue, veuillez réessayer.';
  }

  private inlineMarkdown(value: string): string {
    return value
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  }

  private renderContactCseLinkMarkers(value: string, linkContactCse: boolean): string {
    const rendered = value.replace(
      CONTACT_CSE_LINK_PATTERN,
      linkContactCse
        ? '<a href="/new-chat" data-contact-cse-link="true">$1</a>'
        : '$1',
    );

    return rendered.replace(CONTACT_CSE_MARKER_PATTERN, '');
  }

  private buildSourceGroups(sourcesInput: AiSource[] | undefined, messageIndex = 0): AiSourceGroup[] {
    const sources = Array.isArray(sourcesInput) ? sourcesInput : [];
    const groups: AiSourceGroup[] = [];

    sources.forEach((source: AiSource) => {
      const title = source.title || source.doc_title || 'Document';
      const key = source.doc_uuid || title;
      let group = groups.find(item => item.key === key);

      if (!group) {
        group = {
          key,
          title,
          source_url: source.viewer_url || source.source_url,
          items: [],
        };
        groups.push(group);
      } else if (!group.source_url && (source.viewer_url || source.source_url)) {
        group.source_url = source.viewer_url || source.source_url;
      }

      const passage = this.sourcePassage(source);
      const duplicate = group.items.some(item =>
        (item.article_ref || '') === (source.article_ref || '') &&
        item.passage === passage
      );

      if (!duplicate) {
        group.items.push({
          ...source,
          key: `${messageIndex}-${groups.length - 1}-${group.items.length}`,
          passage,
        });
      }
    });

    return groups;
  }

  private mergeCitedAndRetrievedSources(citedInput?: AiSource[], retrievedInput?: AiSource[]): AiSource[] {
    const cited = Array.isArray(citedInput) ? citedInput : [];
    const retrieved = Array.isArray(retrievedInput) ? retrievedInput : [];

    if (!cited.length) {
      return retrieved;
    }

    return cited.map((source: AiSource) => {
      const match = this.findRetrievedSourceForCitation(source, retrieved);
      const title = source.title || source.doc_title || match?.title || match?.doc_title || 'Document';

      return {
        ...(match || {}),
        ...source,
        title,
        doc_title: source.doc_title || source.title || match?.doc_title || match?.title || 'Document',
        source_url: source.source_url || match?.source_url,
        viewer_url: source.viewer_url || match?.viewer_url,
        content: source.content || match?.content || '',
        highlight_text: source.highlight_text || match?.highlight_text,
        similarity: typeof source.similarity === 'number' ? source.similarity : match?.similarity,
      };
    });
  }

  private findRetrievedSourceForCitation(citation: AiSource, retrieved: AiSource[]): AiSource | undefined {
    const sameDocument = (candidate: AiSource): boolean => {
      if (citation.doc_uuid && candidate.doc_uuid) {
        return citation.doc_uuid === candidate.doc_uuid;
      }

      const citationTitle = citation.doc_title || citation.title;
      const candidateTitle = candidate.doc_title || candidate.title;

      return !!citationTitle && !!candidateTitle && citationTitle === candidateTitle;
    };

    const articleRef = citation.article_ref || '';
    const page = citation.page || null;

    return retrieved.find(candidate =>
      sameDocument(candidate) &&
      !!articleRef &&
      (candidate.article_ref || '') === articleRef
    ) || retrieved.find(candidate =>
      sameDocument(candidate) &&
      !!page &&
      candidate.page === page
    ) || retrieved.find(candidate => sameDocument(candidate));
  }

  private sourcePassage(source: AiSource): string {
    return [
      source.content,
      source.excerpt,
      source.snippet,
      source.text,
    ].find(value => typeof value === 'string' && value.trim())?.trim() || '';
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * The view settles on the question just sent, the answer grows below it,
   * and the reader scrolls at their own pace: nothing moves the thread while
   * the answer is written, as in the usual chat clients.
   */
  private scrollToLatestQuestion() {
    const thread = this.thread?.nativeElement;
    if (!thread) {
      return;
    }

    const rows = thread.querySelectorAll<HTMLElement>('.ai-message-row--user');
    const latest = rows[rows.length - 1];
    if (!latest) {
      return;
    }

    const top = thread.scrollTop + latest.getBoundingClientRect().top - thread.getBoundingClientRect().top - 8;
    if (typeof thread.scrollTo === 'function') {
      thread.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      thread.scrollTop = Math.max(0, top);
    }
  }

  private queueReveal(message: AiMessage, text: string) {
    this.revealTarget = message;
    this.pendingStreamText += text;
    this.scheduleRevealStep();
  }

  /**
   * Next step on the next animation frame. Frames stop while the app is in
   * the background: a plain timer takes over there, so the text still keeps
   * up and nothing piles up for a jump when the screen comes back.
   */
  private scheduleRevealStep() {
    if (this.revealFrame !== undefined) {
      return;
    }

    if (typeof document !== 'undefined' && document.hidden) {
      this.revealFrame = { timer: setTimeout(() => this.revealStep(), 50) };
    } else {
      this.revealFrame = { frame: requestAnimationFrame(() => this.revealStep()) };
    }
  }

  /**
   * One step of the reveal: a share of what is pending, so a burst spreads
   * over a few frames while a steady trickle shows right away.
   */
  private revealStep() {
    this.revealFrame = undefined;
    if (!this.revealTarget || !this.pendingStreamText) {
      return;
    }

    const count = Math.max(3, Math.ceil(this.pendingStreamText.length / 6));
    this.appendRevealed(this.pendingStreamText.slice(0, count));
    this.pendingStreamText = this.pendingStreamText.slice(count);

    if (this.pendingStreamText) {
      this.scheduleRevealStep();
    }
  }

  private flushReveal() {
    this.cancelReveal();
    if (this.revealTarget && this.pendingStreamText) {
      this.appendRevealed(this.pendingStreamText);
    }
    this.pendingStreamText = '';
    this.revealTarget = undefined;
  }

  private cancelReveal() {
    if (this.revealFrame === undefined) {
      return;
    }

    if ('frame' in this.revealFrame) {
      cancelAnimationFrame(this.revealFrame.frame);
    } else {
      clearTimeout(this.revealFrame.timer);
    }
    this.revealFrame = undefined;
  }

  private appendRevealed(text: string) {
    const message = this.revealTarget;
    if (!message) {
      return;
    }

    const reply = message.reply || { main: '' };
    message.reply = reply;
    reply.main = `${reply.main || ''}${text}`;
    this.cdr.detectChanges();
  }

  private openInNewTab(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
